import React, { Component } from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import classnames from 'classnames/bind'
import {
  Button,
  Icon,
  images as toolkitImages,
  Text,
  hooks,
  useApplicationContext,
} from '@adc/ui-adc'
import { ListingUtils } from '@adc/ui-core'
import analytics from '../../services/analytics'
import {
  ACTIVE,
  COMING_SOON,
  FOR_SALE,
  PENDING,
  PENDING_SALE_RESULT,
  UNDER_CONTRACT,
} from '../../statics'
import Overlay from './overlay'
import styles from './index.scss'

const cx = classnames.bind(styles)

export const LISTING_INFORMATION_PHOTO_WIDTH = 750

const VerifiedImage = ({ url, ...restProps }) => {
  const { formatListingImgUrl } = ListingUtils.ListingFormatters
  const { featureFlags } = useApplicationContext()
  const mainImage = hooks.useBackgroundImageWithFallback(
    url,
    featureFlags['fe.resi.enabled-photo-enhancement']
  )

  return (
    <img
      src={formatListingImgUrl(
        `${mainImage || toolkitImages.defaultPropertyImage}`,
        { width: LISTING_INFORMATION_PHOTO_WIDTH }
      )}
      {...restProps}
    />
  )
}

VerifiedImage.propTypes = { url: PropTypes.string.isRequired }

class PropertyGallery extends Component {
  static propTypes = {
    addressTuple: PropTypes.arrayOf(PropTypes.string).isRequired,
    coords: PropTypes.object.isRequired,
    config: PropTypes.object.isRequired,
    images: PropTypes.arrayOf(PropTypes.string).isRequired,
    statusLabel: PropTypes.string,
    onViewChange: PropTypes.func,
    className: PropTypes.string,
    cls: PropTypes.object,
    children: PropTypes.element,
    featureFlags: PropTypes.object,
    listing: PropTypes.object,
    manageSurveyVisibility: PropTypes.func.isRequired,
  }

  static defaultProps = {
    onViewChange: _.noop,
  }

  static toggleLock = (open = true) => {
    if (typeof window === 'undefined') return

    const method = open ? 'add' : 'remove'

    document.documentElement.classList[method](cx('body-noscroll'))
  }

  state = {
    overlay: null,
  }

  setOverlay = overlay => {
    this.setState({ overlay })
    PropertyGallery.toggleLock(true)
  }

  closeOverlay = () => {
    this.setState({ overlay: null })
    PropertyGallery.toggleLock(false)
    this.props.manageSurveyVisibility(true)
  }

  showImageView = () => {
    this.setOverlay('images')
    this.props.onViewChange('images')
    this.props.manageSurveyVisibility(false)
  }

  showMapView = () => {
    this.setOverlay('map')
    this.props.onViewChange('map')
  }

  showStreetView = () => {
    this.setOverlay('street')
    this.props.onViewChange('street')
  }

  statusLabelRole = () => {
    const { statusLabel } = this.props
    if (
      statusLabel === COMING_SOON ||
      _.includes(statusLabel, FOR_SALE) ||
      _.includes(statusLabel, ACTIVE)
    ) {
      return 'success'
    } else if (
      statusLabel === PENDING ||
      statusLabel === UNDER_CONTRACT ||
      _.includes(statusLabel, PENDING_SALE_RESULT)
    ) {
      return 'warning'
    } else {
      return ''
    }
  }

  render() {
    const {
      config,
      className,
      cls = {},
      coords,
      images,
      addressTuple,
      statusLabel,
      children,
      featureFlags = {},
      listing,
    } = this.props

    const { overlay } = this.state

    const videoUrlForThisProperty =
      featureFlags['fe.resi.property-video-config'][listing.primary_property_id]

    const videoButtonClicked = () => {
      analytics.track({ eventName: 'video_button_clicked' })
    }

    return (
      <div
        className={cx('root', className)}
        data-elm-id='property_gallery_section'
      >
        <div className={cx('image-container')}>
          <VerifiedImage
            className={cx(cls.image, 'image')}
            style={{ cursor: images.length > 0 && 'pointer' }}
            data-elm-id='property_gallery_image'
            alt={`${addressTuple[0]} ${addressTuple[1]}`}
            onClick={images.length > 0 ? this.showImageView : _.noop}
            url={images[0]}
          />
        </div>
        <div className={cx('left-button-group')}>
          {images.length > 1 && (
            <Button
              icon
              size='small'
              elmId='property_gallery_ctrl_image'
              onClick={this.showImageView}
            >
              <Icon name='image' iconStyle='light' size='large' />
              <Text weight='medium' as='span' size='tiny' className='u-ml-1'>
                {images.length} Photos
              </Text>
            </Button>
          )}
          {videoUrlForThisProperty && (
            <Button
              icon
              size='small'
              className={cx('ctrl-video')}
              elmId='property_gallery_ctrl_video'
              link={videoUrlForThisProperty}
              target='_blank'
              onClick={videoButtonClicked}
            >
              <Icon name='video' iconStyle='light' size='large' />
            </Button>
          )}
        </div>
        <div className={cx('right-button-group')}>
          <Button
            icon
            size='small'
            elmId='property_gallery_ctrl_street'
            onClick={this.showStreetView}
          >
            <Icon name='street-view' iconStyle='light' size='large' />
            <Text weight='medium' as='span' size='tiny' className='u-ml-1'>
              Street
            </Text>
          </Button>
          <Button
            icon
            size='small'
            elmId='property_gallery_ctrl_map'
            onClick={this.showMapView}
          >
            <Icon name='map-marked' iconStyle='light' size='large' />
            <Text weight='medium' as='span' size='tiny' className='u-ml-1'>
              Map
            </Text>
          </Button>
        </div>

        {statusLabel && (
          <div
            className={cx('status-label', this.statusLabelRole())}
            data-elm-id='property_gallery_status_label'
          >
            <Text color='white' weight='medium'>
              {statusLabel}
            </Text>
          </div>
        )}
        {overlay && (
          <Overlay
            config={config}
            coords={coords}
            images={images}
            addressTuple={addressTuple}
            onViewChange={this.props.onViewChange}
            view={overlay}
            onSave={_.noop}
            onClose={this.closeOverlay}
          >
            {children}
          </Overlay>
        )}
      </div>
    )
  }
}

export default PropertyGallery
